import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { Session, User as SupabaseUser } from "@supabase/supabase-js";
import { supabaseAuth } from "../../tierApi/supabase";
import type { User } from "../../types/entities";

// --- Types ---

interface UserState {
  currentUser: User;
  session: Session | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  signUpLoading: boolean;
  signInLoading: boolean;
  forgotPasswordLoading: boolean;
  updateEmailLoading: boolean;
  updatePasswordLoading: boolean;
  updateNicknameLoading: boolean;
  error: string | null;
  authSuccess: string | null;
}

// --- Async Thunks ---

export const checkAuth = createAsyncThunk<
  Session | null,
  void,
  { rejectValue: string }
>("user/checkAuth", async (_, { rejectWithValue }) => {
  try {
    const { data, error } = await supabaseAuth.auth.getSession();
    if (error) throw error;
    return data.session;
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const signUp = createAsyncThunk<
  { session: Session | null; user: SupabaseUser | null },
  { email: string; password: string; nickname: string },
  { rejectValue: string }
>("user/signUp", async ({ email, password, nickname }, { rejectWithValue }) => {
  try {
    const { data, error } = await supabaseAuth.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: nickname,
        },
      },
    });
    if (error) throw error;
    return data;
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const signIn = createAsyncThunk<
  Session | null,
  { email: string; password: string },
  { rejectValue: string }
>("user/signIn", async ({ email, password }, { rejectWithValue }) => {
  try {
    const { data, error } = await supabaseAuth.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data.session;
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const forgotPassword = createAsyncThunk<
  object | null,
  { email: string },
  { rejectValue: string }
>("user/forgotPassword", async ({ email }, { rejectWithValue }) => {
  try {
    const { data, error } = await supabaseAuth.auth.resetPasswordForEmail(
      email,
      {
        redirectTo: `${window.location.origin}/reset-password`,
      },
    );
    if (error) throw error;
    return data;
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const signOut = createAsyncThunk<null, void, { rejectValue: string }>(
  "user/signOut",
  async (_, { rejectWithValue }) => {
    try {
      const { error } = await supabaseAuth.auth.signOut();
      if (error) throw error;
      return null;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const updateUserEmail = createAsyncThunk<
  SupabaseUser,
  { email: string },
  { rejectValue: string }
>("user/updateUserEmail", async ({ email }, { rejectWithValue }) => {
  try {
    const { data, error } = await supabaseAuth.auth.updateUser({
      email,
    });
    if (error) throw error;
    return data.user;
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const updateUserPassword = createAsyncThunk<
  SupabaseUser,
  { password: string },
  { rejectValue: string }
>("user/updateUserPassword", async ({ password }, { rejectWithValue }) => {
  try {
    const { data, error } = await supabaseAuth.auth.updateUser({
      password,
    });
    if (error) throw error;
    return data.user;
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const updateUserNickname = createAsyncThunk<
  SupabaseUser,
  { nickname: string },
  { rejectValue: string }
>("user/updateUserNickname", async ({ nickname }, { rejectWithValue }) => {
  try {
    const { data, error } = await supabaseAuth.auth.updateUser({
      data: {
        full_name: nickname,
      },
    });
    if (error) throw error;
    return data.user;
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// --- Slice ---

const initialState: UserState = {
  currentUser: {
    id: null,
    nickname: null,
  },
  session: null,
  status: "idle",
  signUpLoading: false,
  signInLoading: false,
  forgotPasswordLoading: false,
  updateEmailLoading: false,
  updatePasswordLoading: false,
  updateNicknameLoading: false,
  error: null,
  authSuccess: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setSession: (state, action: PayloadAction<Session | null>) => {
      state.session = action.payload;
      if (action.payload?.user) {
        state.currentUser = {
          id: action.payload.user.id,
          nickname: action.payload.user.user_metadata?.full_name || null,
        };
      } else {
        state.currentUser = {
          id: null,
          nickname: null,
        };
      }
    },
    clearAuthError: (state) => {
      state.error = null;
    },
    clearAuthSuccess: (state) => {
      state.authSuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.session = action.payload;
        if (action.payload?.user) {
          state.currentUser = {
            id: action.payload.user.id,
            nickname: action.payload.user.user_metadata?.full_name || null,
          };
        } else {
          state.currentUser = {
            id: null,
            nickname: null,
          };
        }
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? null;
        state.currentUser = {
          id: null,
          nickname: null,
        };
      })

      // Sign Up
      .addCase(signUp.pending, (state) => {
        state.signUpLoading = true;
        state.error = null;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.signUpLoading = false;
        state.authSuccess =
          "Account created! Please check your email for verification.";
        if (action.payload.session?.user) {
          state.session = action.payload.session;
          state.currentUser = {
            id: action.payload.session.user.id,
            nickname:
              action.payload.session.user.user_metadata?.full_name || null,
          };
        }
      })
      .addCase(signUp.rejected, (state, action) => {
        state.signUpLoading = false;
        state.error = action.payload ?? null;
      })

      // Sign In
      .addCase(signIn.pending, (state) => {
        state.signInLoading = true;
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.signInLoading = false;
        state.session = action.payload;
        if (action.payload?.user) {
          state.currentUser = {
            id: action.payload.user.id,
            nickname: action.payload.user.user_metadata?.full_name || null,
          };
        }
        state.authSuccess = "Successfully signed in!";
      })
      .addCase(signIn.rejected, (state, action) => {
        state.signInLoading = false;
        state.error = action.payload ?? null;
      })

      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPasswordLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.forgotPasswordLoading = false;
        state.authSuccess = "Password reset email sent! Check your inbox.";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPasswordLoading = false;
        state.error = action.payload ?? null;
      })

      // Sign Out
      .addCase(signOut.fulfilled, (state) => {
        state.session = null;
        state.currentUser = {
          id: null,
          nickname: null,
        };
        state.authSuccess = null;
        state.error = null;
      })

      // Update Email
      .addCase(updateUserEmail.pending, (state) => {
        state.updateEmailLoading = true;
        state.error = null;
      })
      .addCase(updateUserEmail.fulfilled, (state, action) => {
        state.updateEmailLoading = false;
        state.authSuccess = "Email updated successfully!";
        if (state.session) {
          state.session.user = action.payload;
        }
      })
      .addCase(updateUserEmail.rejected, (state, action) => {
        state.updateEmailLoading = false;
        state.error = action.payload ?? null;
      })

      // Update Password
      .addCase(updateUserPassword.pending, (state) => {
        state.updatePasswordLoading = true;
        state.error = null;
      })
      .addCase(updateUserPassword.fulfilled, (state) => {
        state.updatePasswordLoading = false;
        state.authSuccess = "Password updated successfully!";
      })
      .addCase(updateUserPassword.rejected, (state, action) => {
        state.updatePasswordLoading = false;
        state.error = action.payload ?? null;
      })

      // Update Nickname
      .addCase(updateUserNickname.pending, (state) => {
        state.updateNicknameLoading = true;
        state.error = null;
      })
      .addCase(updateUserNickname.fulfilled, (state, action) => {
        state.updateNicknameLoading = false;
        state.authSuccess = "Nickname updated successfully!";
        state.currentUser.nickname =
          action.payload.user_metadata?.full_name || null;
        if (state.session) {
          state.session.user = action.payload;
        }
      })
      .addCase(updateUserNickname.rejected, (state, action) => {
        state.updateNicknameLoading = false;
        state.error = action.payload ?? null;
      });
  },
});

export const { setSession, clearAuthError, clearAuthSuccess } =
  userSlice.actions;

// Selectors — use `any` for state until RootState is available
export const selectCurrentUser = (state: any): User => state.user.currentUser;
export const selectUserNickname = (state: any): string | null =>
  state.user.currentUser.nickname;
export const selectUserId = (state: any): string | null =>
  state.user.currentUser.id;
export const selectSession = (state: any): Session | null => state.user.session;
export const selectAuthStatus = (state: any): string => state.user.status;
export const selectToken = (state: any): string | undefined =>
  state.user.session?.access_token;
export const selectSignUpLoading = (state: any): boolean =>
  state.user.signUpLoading;
export const selectSignInLoading = (state: any): boolean =>
  state.user.signInLoading;
export const selectForgotPasswordLoading = (state: any): boolean =>
  state.user.forgotPasswordLoading;
export const selectUpdateEmailLoading = (state: any): boolean =>
  state.user.updateEmailLoading;
export const selectUpdatePasswordLoading = (state: any): boolean =>
  state.user.updatePasswordLoading;
export const selectUpdateNicknameLoading = (state: any): boolean =>
  state.user.updateNicknameLoading;
export const selectAuthError = (state: any): string | null => state.user.error;
export const selectAuthSuccess = (state: any): string | null =>
  state.user.authSuccess;

export default userSlice.reducer;
